import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Trash2, RefreshCw, AlertTriangle, Truck } from "lucide-react";
import { toast } from "sonner";
import adminApi from "../lib/adminApi";
import { AdminImg } from "../components/AdminImg";
import { ShopAdminGate } from "../components/ShopAdminGate";
import { BuildVersion } from "../components/BuildVersion";
import { SpendGateBanner } from "../components/SpendGateBanner";
import { Button } from "../components/ui/button";
import { OpenInShopifyButton } from "../components/OpenInShopifyButton";
import { FamilyBackfill } from "../components/FamilyBackfill";
import { NameSync } from "../components/NameSync";
import { adminBase, adminHomeLabel } from "../lib/adminBase";

const SHOPIFY_STATUS = { printing: "Being printed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };
const SHOPIFY_STATUS_CLASS = {
  printing: "bg-amber-100 text-amber-800", shipped: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800", cancelled: "bg-rose-100 text-rose-700",
};
const PRINT_STATUSES = ["queued", "printing", "printed"];

function Badge({ children, className, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className || "bg-muted text-muted-foreground"}`}>
      {children}
    </span>
  );
}

function OrdersInner() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [printStatus, setPrintStatus] = useState("");
  const [hideTest, setHideTest] = useState(true);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [busy, setBusy] = useState("");
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [backfill, setBackfill] = useState(null);      // dry-run result awaiting confirmation
  const [backfillTest, setBackfillTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/orders", {
        params: { page, status: status || undefined, print_status: printStatus || undefined, hide_test: hideTest, q: q || undefined, only_missing: onlyMissing || undefined },
      });
      setData(data);
    } catch {
      setError("Could not load orders. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [page, status, printStatus, hideTest, q, onlyMissing]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = (setter) => (v) => {
    setPage(1);
    setter(v);
  };

  const orders = data?.orders || [];
  const flagged = data?.flagged || [];
  const reconcile = data?.reconcile;

  const recheck = async () => {
    setBusy("recheck");
    try {
      const { data } = await adminApi.post("/orders-reconcile");
      const n = data?.reconcile?.flagged || 0;
      toast.success(data?.reconcile?.skipped || `Checked ${data?.reconcile?.checked || 0} orders in Shopify — ${n} flagged as deleted.`);
      await load();
    } catch {
      toast.error("Shopify could not be reached, so nothing was flagged.");
    } finally {
      setBusy("");
    }
  };

  const removeOne = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await adminApi.delete(`/orders/${o.id}`);
      toast.success(`Removed ${data.removed.order_name || `#${o.id}`} — ${data.removed.removed_lines} line(s), ${data.removed.deleted_files} file(s) cleared.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "That order still exists in Shopify — it was not removed." : "Could not remove that order.");
    } finally {
      setBusy("");
    }
  };

  const runBackfill = async (apply) => {
    setBusy("backfill");
    try {
      const { data } = await adminApi.post("/orders-print-status-backfill", { apply, include_test: backfillTest });
      if (apply) {
        setBackfill(null);
        toast.success(`${data.updated} line(s) on ${data.orders} order(s) are now printed.`);
        await load();
      } else {
        setBackfill(data);
        if (!data.lines.length) toast.success("Nothing to update — every shipped order is already marked printed.");
      }
    } catch {
      toast.error("Shopify could not be reached, so nothing was changed.");
    } finally {
      setBusy("");
    }
  };

  const removeAllFlagged = async () => {
    setBusy("bulk");
    setConfirmBulk(false);
    try {
      const { data } = await adminApi.post("/orders-remove-flagged");
      toast.success(`Removed ${data.removed.orders} order(s) and ${data.removed.deleted_files} frozen file(s).`);
      await load();
    } catch {
      toast.error("Could not remove the flagged orders.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="shop-admin-orders-page" className="min-h-screen bg-dots">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <SpendGateBanner />
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Custom duck orders</h1>
          <Link to={adminBase()} data-testid="orders-back-to-app" className="text-sm text-accent font-semibold hover:underline">
            {adminHomeLabel()}
          </Link>
        </div>

        {(flagged.length > 0 || reconcile?.skipped || reconcile?.error) && (
          <div data-testid="orders-flagged-banner" className="mt-6 rounded-2xl border border-rose-300 bg-rose-50 text-rose-900 px-4 py-3">
            <div className="flex items-start gap-3 flex-wrap">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[220px] text-sm">
                {flagged.length > 0 && (
                  <>
                    <p className="font-bold">{flagged.length} order{flagged.length === 1 ? " was" : "s were"} deleted in Shopify</p>
                    <p className="mt-1" data-testid="orders-flagged-list">
                      {flagged.slice(0, 6).map((f) => `${f.order_name || f.order_id.split("/").pop()}${f.is_test ? " (test)" : ""}`).join(" · ")}
                      {flagged.length > 6 ? ` · +${flagged.length - 6} more` : ""}
                    </p>
                    <p className="mt-1 text-xs opacity-80">They no longer count towards ducks printed, maker emails or file freezing. They stay visible here until you remove them.</p>
                  </>
                )}
                {(reconcile?.skipped || reconcile?.error) && (
                  <p data-testid="orders-reconcile-warning" className="mt-1 font-semibold">{reconcile.skipped || `Sweep error: ${reconcile.error}`}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="orders-recheck-btn" variant="outline" size="sm" className="rounded-full gap-2" disabled={!!busy} onClick={recheck}>
                  <RefreshCw className={`h-4 w-4 ${busy === "recheck" ? "animate-spin" : ""}`} /> Re-check now
                </Button>
                {flagged.length > 0 && (
                  <Button data-testid="orders-remove-flagged-btn" size="sm" className="rounded-full gap-2 bg-rose-600 hover:bg-rose-700 text-white" disabled={!!busy} onClick={() => setConfirmBulk(true)}>
                    <Trash2 className="h-4 w-4" /> Remove all {flagged.length}
                  </Button>
                )}
              </div>
            </div>
            {confirmBulk && (
              <div data-testid="orders-bulk-confirm" className="mt-3 rounded-xl bg-background border border-rose-300 px-3 py-2.5 text-sm">
                <p className="font-semibold">
                  Remove {flagged.length} order{flagged.length === 1 ? "" : "s"} from the app
                  {flagged.some((f) => f.is_test) ? ` (${flagged.filter((f) => f.is_test).length} test)` : ""}? Their frozen print files are cleared too. The ducks themselves are untouched.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button data-testid="orders-bulk-confirm-btn" size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white" onClick={removeAllFlagged}>
                    Yes, remove {flagged.length}
                  </Button>
                  <Button data-testid="orders-bulk-cancel-btn" size="sm" variant="outline" className="rounded-full" onClick={() => setConfirmBulk(false)}>
                    Keep them
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div data-testid="orders-backfill-panel" className="mt-6 rounded-2xl border border-border bg-card/70 px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm flex-1 min-w-[220px]">
              <span className="font-semibold">Shipped orders now mark themselves printed.</span>{" "}
              <span className="text-muted-foreground">Use this once to catch up orders that shipped earlier — it shows the list first.</span>
            </p>
            <label data-testid="orders-backfill-test-toggle" className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
              <input type="checkbox" checked={backfillTest} onChange={(e) => setBackfillTest(e.target.checked)} className="h-4 w-4 accent-[color:var(--accent)]" />
              Include test orders
            </label>
            <Button data-testid="orders-backfill-btn" variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={() => runBackfill(false)}>
              {busy === "backfill" ? "Checking…" : "Update print status for shipped orders"}
            </Button>
          </div>
          {backfill && backfill.lines.length > 0 && (
            <div data-testid="orders-backfill-preview" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <p className="font-semibold">
                {backfill.lines.length} line(s) on {backfill.orders} order(s) would change to “printed”:
              </p>
              <ul className="mt-2 space-y-1 text-xs font-mono2 max-h-48 overflow-auto">
                {backfill.lines.map((c, i) => (
                  <li key={`${c.order}-${c.duck_id}-${i}`}>
                    {c.order} · {c.duck_name || c.duck_id?.slice(0, 8)} · {c.height || "?"}mm · {c.print_status} → printed (Shopify: {c.shopify_status}{c.is_test ? ", test" : ""})
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex gap-2">
                <Button data-testid="orders-backfill-apply-btn" size="sm" className="rounded-full" disabled={!!busy} onClick={() => runBackfill(true)}>
                  Apply to {backfill.lines.length} line(s)
                </Button>
                <Button data-testid="orders-backfill-cancel-btn" size="sm" variant="outline" className="rounded-full" onClick={() => setBackfill(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <FamilyBackfill />
        <NameSync />

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="orders-search-input"
              value={q}
              onChange={(e) => reset(setQ)(e.target.value)}
              placeholder="Search order #, customer or duck…"
              className="w-full rounded-full border border-border bg-card pl-9 pr-4 h-10 text-sm outline-none focus:ring-2 ring-accent/40"
            />
          </div>
          <select data-testid="orders-status-filter" value={status} onChange={(e) => reset(setStatus)(e.target.value)}
                  className="rounded-full border border-border bg-card h-10 px-3 text-sm">
            <option value="">All Shopify statuses</option>
            {Object.entries(SHOPIFY_STATUS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <select data-testid="orders-print-status-filter" value={printStatus} onChange={(e) => reset(setPrintStatus)(e.target.value)}
                  className="rounded-full border border-border bg-card h-10 px-3 text-sm capitalize">
            <option value="">All print statuses</option>
            {PRINT_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <label data-testid="orders-hide-test-toggle" className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
            <input type="checkbox" checked={hideTest} onChange={(e) => reset(setHideTest)(e.target.checked)} className="h-4 w-4 accent-[color:var(--accent)]" />
            Hide test orders
          </label>
          <label data-testid="orders-only-missing-toggle" className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
            <input type="checkbox" checked={onlyMissing} onChange={(e) => reset(setOnlyMissing)(e.target.checked)} className="h-4 w-4 accent-[color:var(--accent)]" />
            Only deleted in Shopify
          </label>
        </div>

        <div className="mt-6 rounded-3xl border border-border bg-card/70 overflow-hidden">
          {loading ? (
            <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : error ? (
            <div data-testid="orders-error" className="py-16 text-center text-rose-600">{error}</div>
          ) : orders.length === 0 ? (
            <div data-testid="orders-empty" className="py-16 text-center text-muted-foreground">No orders match these filters.</div>
          ) : (
            <table data-testid="orders-table" className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Customer</th>
                  <th className="px-4 py-3">Ducks</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-2 sm:px-4 py-3 w-12 sm:w-44 text-right"><span className="sr-only">Open in Shopify</span></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.gid} data-testid={`order-row-${o.id}`} className="border-b border-border/60 hover:bg-muted/40">
                    <td className="px-4 py-3 align-top">
                      <Link to={`${adminBase()}/orders/${o.id}`} data-testid={`order-link-${o.id}`} className="font-bold text-foreground hover:underline">
                        {o.order_name || `#${o.id}`}
                      </Link>
                      <div className="text-xs text-muted-foreground mt-1">{(o.created_at || "").slice(0, 10)}</div>
                      {o.is_test && <Badge testid={`order-test-${o.id}`} className="mt-1 bg-zinc-200 text-zinc-700">Test</Badge>}
                      {o.missing_in_shopify && (
                        <div className="mt-1 space-y-1">
                          <Badge testid={`order-missing-${o.id}`} className="bg-rose-100 text-rose-700">Deleted in Shopify</Badge>
                          <button
                            type="button"
                            data-testid={`order-remove-${o.id}`}
                            disabled={!!busy}
                            onClick={() => removeOne(o)}
                            className="flex items-center gap-1 text-xs font-semibold text-rose-700 hover:underline disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> {busy === o.id ? "Removing…" : "Remove from app"}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top hidden sm:table-cell">{o.customer || "—"}</td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2 flex-wrap">
                        {o.ducks.slice(0, 4).map((d) => (
                          <AdminImg key={d.duck_id} testid={`order-thumb-${d.duck_id}`} src={d.thumb} fallback={d.public_thumb} alt={d.duck_name || "duck"}
                               className="h-10 w-10 rounded-lg object-cover bg-muted" />
                        ))}
                        <span className="text-xs text-muted-foreground">
                          {o.ducks.map((d) => `${d.height || "?"}mm`).join(" · ")} · {o.ducks.reduce((n, d) => n + (d.quantity || 1), 0)} pcs
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <Badge testid={`order-status-${o.id}`} className={SHOPIFY_STATUS_CLASS[o.status]}>{SHOPIFY_STATUS[o.status] || o.status}</Badge>
                      <div className="mt-1">
                        <Badge
                          testid={`order-print-${o.id}`}
                          className={o.print_status_auto ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground capitalize"}
                        >
                          <span title={o.print_status_auto ? `Set automatically from Shopify's shipped status${o.print_status_at ? ` on ${o.print_status_at.replace("T", " ").slice(0, 16)}` : ""}` : undefined}>
                            {o.print_status_auto ? "Printed (auto)" : o.ducks[0]?.print_status || "queued"}
                          </span>
                        </Badge>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-3 align-top w-12 sm:w-44 text-right">
                      <OpenInShopifyButton gid={o.gid} name={o.order_name} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && data.pages > 1 && (
          <div className="mt-5 flex items-center justify-center gap-4">
            <Button data-testid="orders-prev-btn" variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <span data-testid="orders-page-indicator" className="text-sm text-muted-foreground">Page {data.page} of {data.pages} · {data.total} orders</span>
            <Button data-testid="orders-next-btn" variant="outline" className="rounded-full" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
        <BuildVersion detailed />
      </main>
    </div>
  );
}

export default function ShopAdminOrders() {
  return (
    <ShopAdminGate>
      <OrdersInner />
    </ShopAdminGate>
  );
}
